import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { X, Calendar, Clock, Users, MapPin, Save, Trash2, Copy, UserMinus } from 'lucide-react';
import SessionWaitlistSection from './SessionWaitlistSection';

export default function EditRecurrenceModal({ isOpen, recurrence, course, onClose, onSuccess }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [sessionDetails, setSessionDetails] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    
    const [formData, setFormData] = useState({
        session_date: '',
        start_time: '',
        end_time: '',
        location: '',
        notes: '',
        max_participants: ''
    });

    useEffect(() => {
        if (isOpen && recurrence) {
            setFormData({
                session_date: recurrence.session_date || '',
                start_time: recurrence.start_time?.slice(0, 5) || '',
                end_time: recurrence.end_time?.slice(0, 5) || '',
                location: recurrence.location || '',
                notes: recurrence.notes || '',
                max_participants: recurrence.max_participants || ''
            });
            fetchParticipants();
            fetchUsers();
        }
    }, [isOpen, recurrence]);

    const fetchParticipants = async () => {
        try {
            const api = getAuthenticatedApi();
            const response = await api.get(`/gym_courses.php?action=get_session&id=${recurrence.id}`);
            setSessionDetails(response.data.session || null);
            setParticipants(response.data.session?.enrollments || []);
        } catch (err) {
            console.error('Errore caricamento partecipanti:', err);
        }
    };

    const fetchUsers = async () => {
        try {
            const api = getAuthenticatedApi();
            const response = await api.get('/users.php?role_name=user');
            setAllUsers(response.data || []);
        } catch (err) {
            console.error('Errore caricamento utenti:', err);
        }
    };

    const handleRemoveParticipant = async (participant) => {
        if (!window.confirm(`Togliere ${participant.user_name} da questa ricorrenza?`)) return;
        try {
            const api = getAuthenticatedApi();
            await api.post('/gym_courses.php?action=cancel_enrollment', {
                enrollment_id: participant.id,
                send_notification: true,
            });
            toast.success('Iscritto rimosso');
            fetchParticipants();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Errore durante la rimozione');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (error) setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (new Date(formData.start_time) >= new Date(formData.end_time)) {
            setError('L\'orario di fine deve essere successivo all\'orario di inizio');
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const api = getAuthenticatedApi();
            
            const response = await api.put(`/gym_courses.php?action=update_session&id=${recurrence.id}`, {
                session_date: formData.session_date,
                start_time: formData.start_time, // Invia solo HH:MM, l'API aggiunge :00
                end_time: formData.end_time,     // Invia solo HH:MM, l'API aggiunge :00
                location: formData.location || null,
                notes: formData.notes || null,
                max_participants: formData.max_participants ? parseInt(formData.max_participants) : null
            });
            
            if (response.data.success) {
                toast.success(t('gym.courses.editRecurrenceModal.messages.saveSuccess'));
                onSuccess();
            } else {
                throw new Error(response.data.error || t('gym.courses.editRecurrenceModal.notifications.updateError'));
            }
            
        } catch (err) {
            console.error('Errore aggiornamento lezione:', err);
            setError(err.response?.data?.error || err.message || t('gym.courses.editRecurrenceModal.notifications.updateError'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(t('gym.courses.editRecurrenceModal.messages.deleteConfirm'))) {
            return;
        }

        try {
            const api = getAuthenticatedApi();
            await api.delete(`/gym_courses.php?action=delete_session&id=${recurrence.id}`);
            
            toast.success(t('gym.courses.editRecurrenceModal.messages.deleteSuccess'));
            onSuccess();
            
        } catch (err) {
            console.error('Errore eliminazione lezione:', err);
            toast.error(t('gym.courses.editRecurrenceModal.notifications.deleteError'));
        }
    };

    const handleDuplicate = async () => {
        try {
            const api = getAuthenticatedApi();
            
            // Crea una nuova sessione con gli stessi dati ma data diversa
            const response = await api.post('/gym_courses.php?action=create_session', {
                course_id: recurrence.course_id,
                session_date: formData.session_date,
                start_time: formData.start_time, // Invia solo HH:MM, l'API aggiunge :00
                end_time: formData.end_time,     // Invia solo HH:MM, l'API aggiunge :00
                max_participants: recurrence.max_participants,
                location: recurrence.location,
                notes: recurrence.notes
            });
            
            if (response.data.success) {
                toast.success(t('gym.courses.editRecurrenceModal.messages.duplicateSuccess'));
                onSuccess();
            } else {
                throw new Error(response.data.error || t('gym.courses.editRecurrenceModal.notifications.duplicateError'));
            }
            
        } catch (err) {
            console.error('Errore duplicazione lezione:', err);
            toast.error(t('gym.courses.editRecurrenceModal.notifications.duplicateError'));
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('it-IT', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (!isOpen || !recurrence) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-4xl">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                            {t('gym.courses.editRecurrenceModal.title')}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-white/65 mt-1">
                            {t('gym.courses.editRecurrenceModal.subtitle', { 
                                courseTitle: course.title, 
                                formattedDate: formatDate(recurrence.session_date) 
                            })}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
                            <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Form Modifica */}
                        <div>
                            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                {t('gym.courses.editRecurrenceModal.sections.details')}
                            </h4>
                            
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Data */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                        <Calendar className="inline w-4 h-4 mr-1" />
                                        {t('gym.courses.editRecurrenceModal.fields.dateLabel')}
                                    </label>
                                    <input
                                        type="date"
                                        name="session_date"
                                        value={formData.session_date}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                        required
                                    />
                                </div>

                                {/* Orari */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                            <Clock className="inline w-4 h-4 mr-1" />
                                            {t('gym.courses.editRecurrenceModal.fields.startTimeLabel')}
                                        </label>
                                        <input
                                            type="time"
                                            name="start_time"
                                            value={formData.start_time}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                            required
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                            <Clock className="inline w-4 h-4 mr-1" />
                                            {t('gym.courses.editRecurrenceModal.fields.endTimeLabel')}
                                        </label>
                                        <input
                                            type="time"
                                            name="end_time"
                                            value={formData.end_time}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Location */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                        <MapPin className="inline w-4 h-4 mr-1" />
                                        {t('gym.courses.editRecurrenceModal.fields.locationLabel')}
                                    </label>
                                    <input
                                        type="text"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleChange}
                                        placeholder="Sala A, Palestra, etc."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Max Partecipanti */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                        <Users className="inline w-4 h-4 mr-1" />
                                        {t('gym.courses.editRecurrenceModal.fields.maxParticipantsLabel')}
                                    </label>
                                    <input
                                        type="number"
                                        name="max_participants"
                                        value={formData.max_participants}
                                        onChange={handleChange}
                                        placeholder="Illimitato"
                                        min="1"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Note */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                        {t('gym.courses.editRecurrenceModal.fields.notesLabel')}
                                    </label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        rows="3"
                                        placeholder={t('gym.courses.editRecurrenceModal.fields.notesPlaceholder')}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Actions */}
                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
                                    >
                                        {loading ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        ) : (
                                            <Save size={16} />
                                        )}
                                        {t('gym.courses.editRecurrenceModal.actions.save')}
                                    </button>
                                    
                                    <button
                                        type="button"
                                        onClick={handleDuplicate}
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                                    >
                                        <Copy size={16} />
                                        {t('gym.courses.editRecurrenceModal.actions.duplicate')}
                                    </button>
                                    
                                    <button
                                        type="button"
                                        onClick={handleDelete}
                                        className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                                    >
                                        <Trash2 size={16} />
                                        {t('gym.courses.editRecurrenceModal.actions.delete')}
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Partecipanti */}
                        <div>
                            {(() => {
                                // Filtra utenti con status 'cancelled' (non devono essere mostrati)
                                const activeParticipants = participants.filter(
                                    p => p.status !== 'cancelled'
                                );
                                return (
                                    <>
                                        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                            {t('gym.courses.editRecurrenceModal.sections.participants', { count: activeParticipants.length })}
                                        </h4>
                                        
                                        {activeParticipants.length > 0 ? (
                                            <div className="space-y-3">
                                                {activeParticipants.map(participant => (
                                        <div
                                            key={participant.id}
                                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center">
                                                    <span className="text-indigo-600 dark:text-indigo-400 font-semibold text-sm">
                                                        {participant.user_name?.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                                                        {participant.user_name}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-white/65">
                                                        {participant.user_email}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                participant.status === 'enrolled' 
                                                    ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400'
                                                    : participant.status === 'attended'
                                                    ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400'
                                                    : 'bg-gray-100 dark:bg-gray-900/20 text-gray-800 dark:text-white/65'
                                            }`}>
                                                {participant.status === 'enrolled' ? t('gym.courses.editRecurrenceModal.participants.status.enrolled') : 
                                                 participant.status === 'attended' ? t('gym.courses.editRecurrenceModal.participants.status.attended') : 
                                                 participant.status === 'absent' ? t('gym.courses.editRecurrenceModal.participants.status.absent') : participant.status}
                                                </span>
                                                {participant.status === 'enrolled' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveParticipant(participant)}
                                                        className="rounded-lg p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                                        title="Togli dagli iscritti"
                                                    >
                                                        <UserMinus size={16} />
                                                    </button>
                                                )}
                                            </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500 dark:text-white/65">
                                        <Users size={48} className="mx-auto mb-2 opacity-50" />
                                        <p>{t('gym.courses.editRecurrenceModal.participants.noParticipants')}</p>
                                    </div>
                                )}
                                </>
                            );
                        })()}
                            <SessionWaitlistSection
                                session={sessionDetails}
                                users={allUsers}
                                onChanged={fetchParticipants}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
